<template>
  <div class="day-view">  
    <div class="day-header"> 
      <button class="back-button" @click="$emit('back')">
        <img
          :src="require('./icons/prevw.svg')"
          :alt="$t('randevu.day_view.back_alt')"
          style="width: 12px; height: 12px; margin-right: 4px"
        />
        {{ $t('randevu.day_view.back') }}
      </button>
 
      <div class="date-nav">
        <button class="nav-button prev" @click="goPrevDay">
          <img
            :src="require('./icons/prevw.svg')"
            :alt="$t('randevu.day_view.prev_day_alt')"
            style="width: 12px; height: 12px"
          />
        </button>

        <h3 class="day-info">{{ dayName }}, {{ day }} {{ monthName }}</h3>

        <button class="nav-button next" @click="goNextDay">
          <img
            :src="require('./icons/nextw.svg')"
            :alt="$t('randevu.day_view.next_day_alt')"
            style="width: 12px; height: 12px"
          />
        </button>
      </div>
    </div>
  
    <div class="hours-grid">
      <div
        v-for="hour in hoursList"
        :key="hour"
        class="hour-block"
        :class="{ now: isCurrentHour(hour) }"
        @click="createNewAppointmentAt(hour)"
      >
        <div class="hour-label">{{ hour }}:00</div>

        <div class="minute-grid">
          <div
            v-for="segment in 12"
            :key="segment"
            class="minute-block"
            :class="getMinuteColorClass(hour, segment * 5)"
          />
        </div>

        <div
          v-for="appt in getAppointmentsAtHour(hour)"
          :key="appt.id"
          class="appointment-name"
          @click.stop="openModal(appt)"
        >
        <span :style="{ color: getStatusColor(appt.status) }">
          {{ formatHourMinute(appt.start_time) }}
        </span>  - {{ appt.customer_name }} - <i style="color: #848484;">{{ appt.expert_name }}</i> 

        <img class="apimg" :src="require('./icons/eye.svg')"/>

        </div>
      </div>
    </div>
 
    <!-- Randevu Detay Modal -->
    <transition name="fade">
      <div v-if="selectedAppointment" class="modal" @click.self="selectedAppointment = null">
        <div class="modal-content">
          <img class="icon" src="./icons/randevular.svg" :alt="$t('randevu.day_view.modal_icon_alt')" />
          <h3 style="font-size: 1rem;">{{ $t('randevu.day_view.modal_title') }}</h3>

          <p><strong>{{ $t('randevu.day_view.customer_label') }}</strong> {{ selectedAppointment.customer_name }}</p>
          <p><strong>{{ $t('randevu.day_view.expert_label') }}</strong> {{ selectedAppointment.expert_name }}</p>
          <p><strong>{{ $t('randevu.day_view.start_label') }}</strong> {{ formatHourMinute(selectedAppointment.start_time) }}</p>
          <p><strong>{{ $t('randevu.day_view.end_label') }}</strong> {{ formatHourMinute(selectedAppointment.end_time) }}</p>
          <p style="font-size: 1.1em;" :style="{ color: getStatusColor(selectedAppointment.status) }">
            <strong>{{ $t('randevu.day_view.status_label') }}</strong> {{ selectedAppointment.status_tr }}
          </p>

          <!-- Durum butonları -->
          <div class="status-actions" v-if="['Onay Bekliyor', 'Onaylandı'].includes(selectedAppointment.status_tr)">
            <button
              v-if="selectedAppointment.status_tr === 'Onay Bekliyor'"
              class="btn approve-btn"
              @click="confirmAppointment(selectedAppointment.id)"
            >
              {{ $t('randevu.day_view.approve') }}
            </button>

            <button
              v-if="['Onay Bekliyor', 'Onaylandı'].includes(selectedAppointment.status_tr)"
              class="btn done-btn"
              @click="completeAppointment(selectedAppointment.id)"
            >
              {{ $t('randevu.day_view.complete') }}
            </button>

            <button
              v-if="['Onay Bekliyor', 'Onaylandı'].includes(selectedAppointment.status_tr)"
              class="btn cancel-btn"
              @click="cancelAppointment(selectedAppointment.id)"
            >
              {{ $t('randevu.day_view.cancel') }}
            </button>
          </div>

          <div v-if="selectedAppointment.services && selectedAppointment.services.length" class="services">
            <p><strong>{{ $t('randevu.day_view.services_label') }}</strong></p>
            <ul>
              <li v-for="svc in selectedAppointment.services" :key="svc.id" class="service-item">
                {{ svc.service_name }}
                <span v-if="svc.quantity > 1">x{{ svc.quantity }}</span>
                – {{ formatPrice(svc.price) }}
              </li>
            </ul>
          </div>

          <div class="modal-actions">
            <button class="edit-btn" @click="editAppointment(selectedAppointment)">
              <img class="mdlimg" :src="require('./icons/duzenle.svg')" />
            </button>
            <button class="close-btn" @click="selectedAppointment = null">{{ $t('randevu.day_view.close') }}</button>
          </div>
        </div>
      </div>
    </transition>


    <!-- Create/Edit Form Modal -->
    <transition name="slide-up">
      <div
        v-if="showForm"
        class="form-modal-overlay"
        @click="closeForm"
      >
        <div class="form-modal-content" @click.stop>
          <div class="close-div">
            <button class="close-button" @click="closeForm">X</button>
        </div> 
          
          <CreateAppointment
            :prefill="formPrefill"
            @cancel="closeForm"
            @success="onFormSuccess"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import CreateAppointment from './CreateAppointment.vue'

export default {
  name: 'CalendarDayView',
  components: { CreateAppointment },
  props: ['day', 'month', 'year'],
  data() {
    const now = new Date()
    return {
      appointments: [],
      selectedAppointment: null,
      currentHour: now.getHours(),
      currentDate: now.getDate(),
      currentMonth: now.getMonth(),
      currentYear: now.getFullYear(), 
      showForm: false,
      formPrefill: null
    }
  },
  computed: {
    monthName() {
      return this.$t(`randevu.months.${this.month}`)
    },
    dayName() {
      const d = new Date(this.year, this.month, this.day)
      return this.$t(`randevu.day_names.${d.getDay()}`)
    },
    hoursList() {
      return Array.from({ length: 18 }, (_, i) => i + 6)
    }
  },
  mounted() {
    this.fetchAppointmentsForDay()
  },
  watch: {
    day: 'fetchAppointmentsForDay',
    month: 'fetchAppointmentsForDay',
    year: 'fetchAppointmentsForDay'
  },
  methods: {
    async fetchAppointmentsForDay() {
      try {
        const { data } = await axios.get('/promo/user/appointments/by-day', {
          params: { year: this.year, month: this.month + 1, day: this.day }
        })
        this.appointments = data
      } catch (e) {
        console.error('Randevular getirilemedi.', e)
        this.appointments = []
      }
    },
    getStatusColor(status) {
      const colorMap = {
        pending: '#CCA66A',
        confirmed: '#CCA66A',
        completed: '#1CC655',
        cancelled: '#FF0000'
      };
      return colorMap[status] || '#fff'; 
    },
    goPrevDay() {
      const prev = new Date(this.year, this.month, this.day - 1)
      this.$emit('prev-day', {
        day: prev.getDate(),
        month: prev.getMonth(),
        year: prev.getFullYear()
      })
    },
    goNextDay() {
      const next = new Date(this.year, this.month, this.day + 1)
      this.$emit('next-day', {
        day: next.getDate(),
        month: next.getMonth(),
        year: next.getFullYear()
      })
    },
    isCurrentHour(hour) {
      return (
        hour === this.currentHour &&
        this.day === this.currentDate &&
        this.month === this.currentMonth &&
        this.year === this.currentYear
      )
    },
    getAppointmentsAtHour(hour) {
      return this.appointments.filter(a => {
        const s = new Date(a.start_time)
        const e = new Date(a.end_time)
        return s.getHours() === hour || (s.getHours() < hour && e.getHours() > hour)
      })
    },
    getMinuteColorClass(hour, minute) {
      const match = this.appointments.find(a => {
        const s = new Date(a.start_time)
        const e = new Date(a.end_time)
        const st = s.getHours() * 60 + s.getMinutes()
        const et = e.getHours() * 60 + e.getMinutes()
        return hour * 60 + minute >= st && hour * 60 + minute < et
      })
      return match ? 'active-minute' : ''
    },
    openModal(a) {
      this.selectedAppointment = a
    },   
    openCreateForm(datetime) {
      this.formPrefill = {
        start_time: datetime.toISOString().slice(0, 16),
        end_time: new Date(datetime.getTime() + 30 * 60000).toISOString().slice(0, 16)
      }
      this.showForm = true
      this.selectedAppointment = null
    },
    openEditForm(appt) {
      this.formPrefill = {
        id: appt.id,
        customer_id: appt.customer_id,
        customer_name: appt.customer_name, 
        expert_id: appt.expert_id,
        expert_name: appt.expert_name,  
        start_time: appt.start_time.slice(0, 16),
        end_time: appt.end_time.slice(0, 16),
        notes: appt.notes || '',
        services: appt.services || [{ service_id: null, quantity: 1 }]
      }
      this.showForm = true
      this.selectedAppointment = null
    },
    closeForm() {
      this.showForm = false
      this.formPrefill = null
    },
    onFormSuccess() {
      this.closeForm()
      this.fetchAppointmentsForDay()
    },
    createNewAppointmentAt(hour) {
      const date = new Date(this.year, this.month, this.day, hour, 0)
      this.openCreateForm(date)
    },
    editAppointment(a) {
      this.openEditForm(a)
    },
    formatPrice(value, currency_id) { 
      let currencyCode;
      switch (currency_id) {
        case 1:
          currencyCode = 'TRY'; 
          break;
        case 2:
          currencyCode = 'USD'; 
          break;
        case 3:
          currencyCode = 'EUR';  
          break;
        default:
          currencyCode = 'TRY';
      } 
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    async deleteAppointment(id) {
      if (!confirm(this.$t('randevu.day_view.delete_confirm'))) return
      try {
        await axios.delete(`/promo/user/appointments/${id}`)
        this.selectedAppointment = null
        this.fetchAppointmentsForDay()
      } catch (e) {
        console.error('Silme işlemi başarısız.', e)
      }
    },
 
    async confirmAppointment(id) {
      try {
        await axios.post(`/promo/user/appointments/${id}/confirm`)
        this.fetchAppointmentsForDay()
        this.selectedAppointment = null
      } catch (e) {
        console.error('Onaylama başarısız.', e)
      }
    },
    async completeAppointment(id) {
      try {
        await axios.post(`/promo/user/appointments/${id}/complete`)
        this.fetchAppointmentsForDay()
        this.selectedAppointment = null
      } catch (e) {
        console.error('Tamamlama başarısız.', e)
      }
    },
    async cancelAppointment(id) {
      try {
        await axios.post(`/promo/user/appointments/${id}/cancel`)
        this.fetchAppointmentsForDay()
        this.selectedAppointment = null
      } catch (e) {
        console.error('İptal işlemi başarısız.', e)
      }
    }
  }
}
</script>

<style scoped>
.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5em;
}

.date-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.2rem;
  transition: transform 0.15s ease;
  min-width: 3em;
}
.nav-button:hover {
  transform: scale(1.15);
} 
.hours-grid {
  display: flex;
  flex-direction: column;
  gap: 1em;
}

.hour-block {
  background-color: #050505;
  padding: 1rem 10px;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  max-height: 220px;  
  overflow-y: auto; 
  display: flex;
  flex-direction: column;
}

.hour-block.now {
  color: white;
  border-color: #106E35;
  font-weight: bold;
}

.hour-label {
  font-weight: 500;
  margin-bottom: 4px;
  font-size: 1.3em;
}

.minute-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 1px;
  margin-bottom: 5px;
}

.minute-block {
  height: 2px;
  background-color: #1a1a1a;
}

.minute-block.active-minute {
  background-color: #106E35;
}

.appointment-name {
  cursor: pointer;
  font-size: 1em;
  margin-top: 10px;
  color: #dddddd;
}
.appointment-name:hover { 
  color: #fff;
}
.apimg {  height: 8px; margin-left: 4px; 
  opacity: 0.2;
}
.appointment-name:hover .apimg { 
  opacity: 1;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: linear-gradient(45deg, #020202, #080808 );
  padding: 2rem;
  border-radius: 8px;
  border: 1px solid #0B3018;
  max-width: 500px; 
  min-width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
}
.modal-content p, h3{
   margin: 2px;
}
.modal-content h3{
   margin: 2px;
   color: #106E35 ;
   font-size: 1.1rem;
   font-weight: 600;
}

.modal-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  gap: 8px;
}

.edit-btn,
.delete-btn {
  background: none; 
  padding: 0.5rem 1em;
  border: none;
  cursor: pointer;
  min-width: 4rem;
} 
.close-btn {
  background: none; 
  padding: 0.5rem 1em; 
  cursor: pointer;
  min-width: 4rem;
}
.mdlimg{ max-width: 1rem;}

.day-info {
  font-size: 1rem;
  font-weight: 500;
  color: #118a1b;
}

.back-button {
  border: none;
  min-width: 5em;
  min-height: 3em;
  background: linear-gradient(45deg, #023032, #011111); 
  transition: all 0.2s ease;
  font-weight: 300;
  border-radius: px;
  border: solid 1px #011111;
  padding: 5px;
}

.back-button:hover {
  transform: scale(1.05);
  color: white;
  box-shadow: 0 5px 12px rgba(0, 104, 42, 0.2);
}

/* Ortak Modal Kapatma Butonu */
.modal-close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 1.2rem;
  cursor: pointer;
}
.modal-close-button:hover {
  color: #f44336;
}

/* Form Modal (BottomMenu stili) */
.form-modal-overlay { 
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 9999;
}

.form-modal-content {
  position: relative;
  margin-top: 12rem;
  width: 100%;
  max-width: 728px;
  background: linear-gradient(45deg, #020202, #080808, #030b0b);
  border-radius: 10px 10px 0 0;
  border-top: 1px solid #022121;
  border-left: 1px solid #022121;
  border-right: 1px solid #022121;
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.5);
  padding: 1em 1em 5em 1em;
  overflow-y: auto;
  max-height: 75vh;
  min-height: 40vh;
}

.close-div {
  position: sticky;
  border-radius: 15px 15px 0 0;
  top: 0;
  display: flex;
  justify-content: flex-end;
  z-index: 1;
  padding: 0.5rem;
}

.close-button {
  background-color: #000000;
  color: #ffffff;
  padding: 0.8rem;
  min-width: 3rem;
  max-width: 3rem;
  border-radius: 2rem;
  opacity: 0.8;
  border: 1px solid #444444;
  cursor: pointer;
  transition: transform 0.3s ease;
}
.close-button:hover {
  opacity: 1;
  transform: scale(1.1);
}

/* Animasyonlar */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
.slide-up-enter-active, .slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-up-enter, .slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}


.status-actions {
  margin: 12px 0 6px;
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
}
.approve-btn {
  background: #065959; 
}
.done-btn {
  background: #106E35; 
}
.cancel-btn {
  background: #9B0101; 
}
</style>
